/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Main file for the DateRangeInput
 */

import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import {
  isInIgnored,
  isOnIgnored,
  isOverIgnored,
} from '../DateRangeInput/helpers/dateWithinHelpers'
import { useHandleDate } from '../DateRangeInput/helpers/useHandleDate'

import { DateRangeInputComponent } from './DateRangeInputComponent'
import { Errors, UndefinedOrDate, WeekStartsOn } from './types'

export interface ShortTermDateRangeInputProps {
  calendarDisplayNum?: number
  dateButtonStyle?: any
  children?: ReactNode | ReactNode[]
  disableForeverFrom?: Date
  disableForeverTo?: Date
  disabledRanges?: ReadonlyArray<readonly [startDate: Date, endDate: Date]>
  endDate?: Date
  endInputPlaceholder?: string
  errors?: Errors[]
  errorCallback?: (
    selectedDate: Date,
    startDate: UndefinedOrDate,
    endDate: UndefinedOrDate
  ) => Errors[]
  initDate?: Date
  isMobileScrollCalendar?: boolean
  minimumStay?: number
  setEndDate?: any
  setStartDate: any
  showCalendarOnActiveInput?: boolean
  showDayOfWeekHeader?: boolean
  showTextInput?: boolean
  showTwoMonths?: boolean
  singleCalendar?: boolean
  startDate?: Date
  startInputPlaceholder?: string
  subtitle?: string
  title?: string
  transparentBackground?: boolean
  visibleTargetMonth?: Date
  weekStartsOn?: WeekStartsOn
}

export const ShortTermDateRangeInput = ({
  calendarDisplayNum,
  dateButtonStyle,
  disableForeverFrom = undefined,
  disableForeverTo = undefined,
  disabledRanges,
  endDate,
  endInputPlaceholder = 'MM/DD/YYYY',
  errors = [],
  errorCallback = () => [],
  initDate = new Date(),
  isMobileScrollCalendar,
  minimumStay,
  setEndDate: setEndDateBase = () => {},
  setStartDate: setStartDateBase,
  showDayOfWeekHeader = false,
  showTextInput = true,
  showTwoMonths = false,
  singleCalendar = false,
  startDate,
  startInputPlaceholder = 'MM/DD/YYYY',
  subtitle,
  title,
  transparentBackground = false,
  visibleTargetMonth,
  weekStartsOn = 0,
}: ShortTermDateRangeInputProps) => {
  const [targetMonth, setTargetMonth] = useState<Date>(visibleTargetMonth ?? initDate)
  const [cbErrors, setCbErrors] = useState<Errors[]>([])
  const mainRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    visibleTargetMonth && setTargetMonth(visibleTargetMonth)
  }, [visibleTargetMonth])

  const setStartDate = (date: UndefinedOrDate) => {
    const isIgnored = isOnIgnored(
      disabledRanges,
      disableForeverTo,
      disableForeverFrom,
      date ?? new Date()
    )

    if (date && isIgnored) {
      setCbErrors([{ type: 'start', message: 'Date is unavailable' }])
      return
    }
    setStartDateBase(date)
  }

  const setEndDate = (date: UndefinedOrDate) => {
    if (startDate && date) {
      if (isOverIgnored(disabledRanges, startDate, date)) {
        setCbErrors([{ type: 'end', message: 'Date is unavailable' }])
      }
    } else if (date && isInIgnored(disabledRanges, date)) {
      setCbErrors([{ type: 'end', message: 'Date is unavailable' }])
      return
    }

    setEndDateBase(date)
  }

  const setDate = useHandleDate(
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    errorCallback,
    setCbErrors,
    disabledRanges,
    singleCalendar
  )

  const allErrors = useMemo(() => [...errors, ...cbErrors], [errors, cbErrors])

  return (
    <DateRangeInputComponent
      allErrors={allErrors}
      calendarDisplayNum={calendarDisplayNum}
      dateButtonStyle={dateButtonStyle}
      disableForeverFrom={disableForeverFrom}
      disableForeverTo={disableForeverTo}
      disabledRanges={disabledRanges}
      endDate={endDate}
      endInputPlaceholder={endInputPlaceholder}
      isMobileScrollCalendar={isMobileScrollCalendar}
      mainRef={mainRef}
      minimumStay={minimumStay}
      setDate={setDate}
      setEndDate={setEndDate}
      setStartDate={setStartDate}
      setTargetMonth={setTargetMonth}
      showDayOfWeekHeader={showDayOfWeekHeader}
      showTextInput={showTextInput}
      showTwoMonths={showTwoMonths}
      singleCalendar={singleCalendar}
      startDate={startDate}
      startInputPlaceholder={startInputPlaceholder}
      subtitle={subtitle}
      targetMonth={targetMonth}
      title={title}
      transparentBackground={transparentBackground}
      weekStartsOn={weekStartsOn}
    />
  )
}
